import { useState, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import {
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { loginRequest, graphScopes, silentRedirectUri } from "./msalConfig";
import { fallbackToRedirect, saveCurrentRoute } from "./redirectFallback";
import { getEnv } from "../env";

const ADMIN_GROUP = getEnv("VITE_AZURE_AD_GROUP_ADMINS");
const USER_GROUP = getEnv("VITE_AZURE_AD_GROUP_USERS");

export type UserRole = "admin" | "user" | null;

export interface AuthUser {
  name: string;
  email: string;
  role: UserRole;
  avatarUrl: string | null;
}

export function resolveRole(
  groups: string[],
  adminGroup: string,
  userGroup: string,
): UserRole {
  if (groups.includes(adminGroup)) return "admin";
  if (groups.includes(userGroup)) return "user";
  return null;
}

export function useAuth(options?: { redirectUri?: string; interaction?: "redirect" | "popup" }) {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const account = accounts[0] ?? null;
  // Retorno de las llamadas silenciosas: una pagina vacia, nunca la app (ver
  // silentRedirectUri). El redirect de pagina completa sigue volviendo a la raiz.
  const silentRedirect = options?.redirectUri ?? silentRedirectUri;
  const interaction = options?.interaction ?? "redirect";
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Fetch avatar from Microsoft Graph (cached in sessionStorage)
  useEffect(() => {
    if (!account || inProgress !== InteractionStatus.None) return;

    const cacheKey = `avatar_${account.homeAccountId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      setAvatarUrl(cached);
      return;
    }

    instance
      .acquireTokenSilent({ ...graphScopes, account, redirectUri: silentRedirect })
      .then((response) =>
        fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
          headers: { Authorization: `Bearer ${response.accessToken}` },
        }),
      )
      .then((res) => {
        if (!res.ok) throw new Error("No photo");
        return res.blob();
      })
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          sessionStorage.setItem(cacheKey, dataUrl);
          setAvatarUrl(dataUrl);
        };
        reader.readAsDataURL(blob);
      })
      .catch(() => setAvatarUrl(null));
  }, [account, instance, inProgress, silentRedirect]);

  const getRole = (): UserRole => {
    if (!account) return null;
    const groups: string[] =
      ((account.idTokenClaims as Record<string, unknown>)?.groups as string[]) ?? [];
    return resolveRole(groups, ADMIN_GROUP, USER_GROUP);
  };

  const user: AuthUser | null = account
    ? {
        name: account.name ?? "",
        email: account.username ?? "",
        role: getRole(),
        avatarUrl,
      }
    : null;

  const login = useCallback(() => {
    // Guardar la ruta para volver a ella tras el login (enlaces directos /link/...).
    // En /login no: tras autenticar debe ir a la home, no de vuelta al login.
    if (window.location.pathname !== "/login") saveCurrentRoute();
    instance.loginRedirect(loginRequest).catch((e) => console.error(e));
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect().catch((e) => console.error(e));
  }, [instance]);

  // Patron oficial Microsoft: acquireTokenSilent → interaccion en InteractionRequired.
  // App principal: acquireTokenRedirect (pagina completa, sin popups).
  // Widgets iframe: acquireTokenPopup (el redirect no funciona en iframe).
  // See: https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-acquire-token
  const getToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error("Not authenticated");

    const acquire = async (forceRefresh: boolean) => {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
        redirectUri: silentRedirect,
        forceRefresh,
      });
      return response.idToken;
    };

    try {
      const idToken = await acquire(false);
      // Check if ID token is expired or about to expire (5 min buffer)
      const claims = JSON.parse(atob(idToken.split(".")[1])) as { exp?: number };
      if (claims.exp && claims.exp * 1000 - Date.now() < 5 * 60 * 1000) {
        return await acquire(true);
      }
      return idToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        if (interaction === "popup") {
          // Widgets en iframe: un redirect de pagina completa no funciona ahi.
          const response = await instance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
          return response.idToken;
        }
        // App principal: redirect de pagina completa (patron gesdoc). Nunca popups.
        return fallbackToRedirect(instance, account);
      }
      // Error transitorio (red, timeout): relanzar sin redirigir — no echar al
      // usuario por un blip de red.
      throw err;
    }
  }, [instance, account, silentRedirect, interaction]);

  return { isAuthenticated, user, login, logout, getToken, inProgress };
}
